'use client';

import Link from '~/(tenant)/_components/tenant-link';
import Image from 'next/image';

import { GraduationCap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  HorizontalCardScroller,
  SectionHeader,
} from '~/(tenant)/_components/native/_primitives';

import type { ClassCategoriesSectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';
import type { NativeClassCategory } from '~/(tenant)/_lib/server/load-native-class-categories';

interface ClassCategoriesSectionProps {
  config: ClassCategoriesSectionConfig;
  /**
   * Class types prefetched server-side (anon has no SELECT grant on
   * class_types — see load-native-class-categories.ts).
   */
  classTypes: NativeClassCategory[];
}

export function ClassCategoriesSection({
  config,
  classTypes,
}: ClassCategoriesSectionProps) {
  const t = useNativeTokens();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const byId = new Map(classTypes.map((ct) => [ct.id, ct]));

  // Preserve the tenant's curated order (config.items), dropping any reference
  // that no longer resolves (hidden/deleted class type).
  const tiles = config.items
    .map((item) => {
      const ct = byId.get(item.classTypeId);
      if (!ct) return null;
      // Prefer the curated label, falling back to the class type name. On Arabic
      // prefer Arabic sources (label_ar → name_alt) before the English override.
      const label = isAr
        ? item.label_ar?.trim() ||
          ct.name_alt?.trim() ||
          item.label?.trim() ||
          ct.name
        : item.label?.trim() || ct.name;
      return { id: ct.id, label, imageUrl: ct.image_url };
    })
    .filter((tile): tile is { id: string; label: string; imageUrl: string | null } => tile !== null);

  if (tiles.length === 0) return null;

  const title =
    (isAr && config.title_ar?.trim()) || config.title?.trim() || null;
  const eyebrow =
    (isAr && config.eyebrow_ar?.trim()) || config.eyebrow?.trim() || null;

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={
          eyebrow ?? (
            <Trans i18nKey="common:nativeHome.classCategoriesEyebrow" defaults="Our classes" />
          )
        }
        title={
          title ?? (
            <Trans i18nKey="common:nativeHome.classCategoriesTitle" defaults="Class categories" />
          )
        }
      />

      <HorizontalCardScroller gap={20}>
        {tiles.map((tile) => (
          <Link
            key={tile.id}
            href={`/class-categories/${tile.id}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
              width: 92,
              textDecoration: 'none',
              color: t.foreground,
            }}
          >
            <div
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: t.line,
              }}
            >
              {tile.imageUrl ? (
                <Image
                  src={tile.imageUrl}
                  alt={tile.label}
                  fill
                  className="object-cover"
                  unoptimized
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: t.muted,
                  }}
                >
                  <GraduationCap size={32} strokeWidth={1.2} />
                </div>
              )}
            </div>
            <span
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 14,
                lineHeight: 1.2,
                textAlign: 'center',
                color: t.foreground,
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {tile.label}
            </span>
          </Link>
        ))}
      </HorizontalCardScroller>
    </section>
  );
}
